import Divider from '@material-ui/core/Divider';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import { Theme } from '@material-ui/core/styles/createMuiTheme';
import createStyles from '@material-ui/core/styles/createStyles';
import withStyles, { WithStyles } from '@material-ui/core/styles/withStyles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import React, { ChangeEvent, PureComponent } from 'react';
import { injectIntl, InjectedIntlProps } from 'react-intl';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import { Action, Dispatch } from 'redux';
import messages from '../../../../intl/messages';
import {
  AirSegmentDetails,
  OptionId,
  SegmentTypes,
} from '../../../../models/itinerary';
import { AppState } from '../../../../store/model';
import {
  getSegmentType,
  getTravellerCount,
} from '../../../../store/reducers/itinerary/selectors';
import { findSegment as findSegmentDetails } from '../../../../store/reducers/segment-details/selectors';
import { getSegmentOption } from '../../../../store/reducers/segment-options/selectors';
import { selectOption } from '../../../../store/reducers/selected-options/actions';
import { isSelected } from '../../../../store/reducers/selected-options/selectors';
import { formatTravelTime } from '../../../../utils/format';
import { AllocationColumn } from './Columns/AllocationColumn';
import { DurationColumn } from './Columns/DurationColumn';
import { ProviderColumn } from './Columns/ProviderColumn/ProviderColumn';
import { Header } from './Header/Header';
import { RateDetails } from './Rates/RateDetails';
import { RateSummary } from './Rates/RateSummary';
import { RouteType as Route } from './Route/Types/RouteType';
import { Section } from './Section';

interface OwnProps {
  index: number;
  optionId: OptionId;
  segmentId: number;
  isRecommended: boolean;
  onFareToggle(): void;
}

interface StateProps {
  type: SegmentTypes;
  travellerCount: number;
  days?: number;
  nights?: number;
  totalDuration?: string;
  selected: boolean;
}

interface DispatchProps {
  onOptionSelect(): void;
}

interface State {
  isFareExpanded: boolean;
}

interface OptionMobileProps
  extends OwnProps,
    StateProps,
    DispatchProps,
    WithStyles<typeof styles>,
    InjectedIntlProps {}

class OptionMobileBase extends PureComponent<OptionMobileProps, State> {
  constructor(props: OptionMobileProps) {
    super(props);
    this.state = {
      isFareExpanded: false,
    };
  }

  public render() {
    const {
      classes,
      isRecommended,
      index,
      segmentId,
      optionId,
      type,
    } = this.props;

    const { isFareExpanded } = this.state;

    return (
      <div className={classes.container}>
        <Paper className={classes.panel} elevation={1}>
          <Header recommended={isRecommended} type={type} index={index} />
          <Divider />
          <Section>
            <ProviderColumn optionId={optionId} segmentId={segmentId} />
          </Section>
          <Divider />
          <Section>
            <Grid
              item
              container
              direction="row"
              justify="center"
              alignItems="center"
              wrap="nowrap"
            >
              <Route optionId={optionId} segmentId={segmentId} />
            </Grid>
          </Section>
          <Divider />
          <Section>
            <DurationColumn duration={this.getDuration()} />
            <AllocationColumn allocation={this.getAllocation()} />
          </Section>
        </Paper>
        <ExpansionPanel onChange={this.onExpansionPanelChange}>
          <ExpansionPanelSummary
            className={classes.expansionPanel}
            expandIcon={<ExpandMoreIcon />}
            classes={{ content: classes.expansionPanelContent }}
          >
            <RateSummary
              type={type}
              expanded={isFareExpanded}
              segmentId={Number(segmentId)}
              optionId={optionId}
            />
          </ExpansionPanelSummary>
          <ExpansionPanelDetails>
            <RateDetails
              optionId={optionId}
              segmentId={Number(segmentId)}
              type={type}
              isMobile={true}
            />
          </ExpansionPanelDetails>
        </ExpansionPanel>
      </div>
    );
  }

  private onExpansionPanelChange = (
    _event: ChangeEvent<{}>,
    expanded: boolean,
  ) => {
    this.setState({ isFareExpanded: expanded });
    // TODO get animation time and pass it in rather than the magic number
    setTimeout(this.props.onFareToggle, 250);
  };

  private getAllocation = (): string => {
    const {
      intl: { formatMessage },
      travellerCount,
      type,
    } = this.props;
    switch (type) {
      case 'Air':
        return formatMessage(messages.flight.passengers, {
          numberOfPassengers: travellerCount,
        });
        break;
      case 'Hotel':
        return formatMessage(messages.hotel.rooms, {
          numberOfRooms: travellerCount,
        });
      case 'Car':
        return formatMessage(messages.car.allocation, { count: 1 });
    }
  };

  private getDuration = (): string => {
    const { type } = this.props;
    switch (type) {
      case 'Air':
        return this.getAirDuration();
      case 'Hotel':
        return this.getHotelDuration();
      case 'Car':
        return this.getCarDuration();
    }
  };

  private getAirDuration = (): string => {
    const { totalDuration } = this.props;

    return formatTravelTime(totalDuration as string);
  };

  private getHotelDuration = (): string => {
    const {
      intl: { formatMessage },
      nights,
    } = this.props;

    return formatMessage(messages.hotel.nights, {
      numberOfNights: nights,
    });
  };

  private getCarDuration = (): string => {
    const {
      intl: { formatMessage },
      days,
    } = this.props;

    return formatMessage(messages.car.days, { days });
  };
}

const mapStateToProps = (
  state: AppState,
  { segmentId, optionId }: OwnProps,
): StateProps => {
  const type = getSegmentType(segmentId)(state) || 'Air';
  const { days, nights } = findSegmentDetails(segmentId)(state);

  const { totalDuration } = getSegmentOption(segmentId, optionId)(
    state,
  ) as AirSegmentDetails;

  return {
    type,
    days,
    nights,
    totalDuration,
    travellerCount: getTravellerCount(state),
    selected: isSelected(Number(segmentId), optionId)(state),
  };
};

const mapDispatchToProps = (
  dispatch: Dispatch<Action>,
  { segmentId, optionId }: OwnProps,
): DispatchProps => ({
  onOptionSelect: () => {
    dispatch(selectOption(Number(segmentId), optionId));
  },
});

// styled
const styles = (theme: Theme) => {
  const {
    spacing: { unit: spacing },
  } = theme;

  return createStyles({
    container: {
      display: 'flex',
      flexDirection: 'column',
      maxWidth: '100%',
      width: '100%',
      boxSizing: 'border-box',
      paddingLeft: spacing * 2,
      paddingRight: spacing * 2,
      padding: spacing * 2,
    },
    panel: {
      borderRadius: 2, // ExpansionPanel border-bottom-radius
    },
    multipleStopsText: {
      color: theme.zeno.semantic.error.main,
      textAlign: 'center',
    },
    noWrap: {
      whiteSpace: 'nowrap',
    },
    locationIcon: {
      height: spacing * 2,
      width: spacing * 2,
    },
    expansionPanel: {
      paddingLeft: spacing * 2,
      paddingRight: spacing * 2,
    },
    expansionPanelContent: {
      width: '100%',
    },
  });
};

const enhance = compose<OptionMobileProps, OwnProps>(
  injectIntl,
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withStyles(styles),
);

export const OptionMobile = enhance(OptionMobileBase);

OptionMobile.displayName = 'OptionMobile';
